import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, timer, catchError, tap, of, switchMap } from 'rxjs';
import { MerchantStore } from '../stores';
import { MerchantApi } from '../apis';
import type { MerchantDetailsResponse } from '../models';

/**
 * MerchantService - Business logic for merchant data management
 *
 * Responsibilities:
 * - Load and cache merchant details
 * - Auto-refresh merchant data (every 30 minutes)
 * - Smart caching to reduce API calls
 * - Rate limiting to prevent API abuse
 * - Event-driven refresh for immediate updates
 * - Error handling and recovery
 *
 * Usage:
 * - Called on app initialization (if authenticated)
 * - Auto-refresh runs in background (30min interval)
 * - Manual refresh available via refresh() method (rate limited)
 * - Event-driven refresh via refreshOnEvent() after user actions
 * - Cleared on logout via clearMerchantData()
 */
@Injectable({
  providedIn: 'root',
})
export class MerchantService {
  private readonly merchantStore = inject(MerchantStore);
  private readonly merchantApi = inject(MerchantApi);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Auto-refresh interval: 30 minutes
   * Reduced from 5 minutes to prevent API rate limiting (HTTP 429)
   */
  private readonly AUTO_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

  /**
   * Minimum interval between manual refresh calls: 2 minutes
   * Prevents burst API calls from event-driven refreshes
   */
  private readonly MIN_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes

  /**
   * Timestamp of last refresh call
   * Used for rate limiting manual/event-driven refreshes
   */
  private lastRefreshTime = 0;

  // Track auto-refresh state
  private isAutoRefreshActive = false;

  /**
   * Load merchant details from API
   *
   * @param accountId - Merchant account ID (required)
   * @param forceRefresh - Skip cache and force API call
   * @returns Observable of merchant details response
   *
   * Uses smart caching:
   * - If cache is valid and not forcing refresh, returns cached data
   * - Otherwise fetches fresh data from API
   * - Updates store on successful fetch
   */
  loadMerchantDetails(
    accountId: string,
    forceRefresh = false,
  ): Observable<MerchantDetailsResponse> {
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && this.merchantStore.isCacheValid() && this.merchantStore.hasMerchant()) {
      return of({
        success: true,
        merchant: this.merchantStore.merchant()!,
        is_out_delivery_range: false,
        migrate_to_mobile_money: false,
      });
    }

    this.merchantStore.setLoading(true);

    return this.merchantApi.getMerchantDetails(accountId).pipe(
      tap((response) => {
        if (response.success && response.merchant) {
          this.merchantStore.setMerchant(response.merchant, accountId);
        }
        this.merchantStore.setLoading(false);
      }),
      catchError((error) => {
        this.merchantStore.setError('Failed to load merchant details');
        this.merchantStore.setLoading(false);
        throw error;
      }),
    );
  }

  /**
   * Start automatic refresh of merchant data
   *
   * Refreshes merchant data every 30 minutes to keep balance and other info current
   * Reduced from 5 minutes to prevent API rate limiting (HTTP 429)
   * Automatically stops when component/service is destroyed
   * Only runs one instance at a time (prevents duplicate intervals)
   * Uses stored accountId from previous loadMerchantDetails call
   *
   * Note: For immediate updates after user actions, use refreshOnEvent() instead
   */
  startAutoRefresh(): void {
    if (this.isAutoRefreshActive) {
      return;
    }

    // Check if we have an accountId stored
    const storedAccountId = this.merchantStore.accountId();
    if (!storedAccountId) {
      return;
    }

    this.isAutoRefreshActive = true;

    // Start timer that fires every 30 minutes
    timer(this.AUTO_REFRESH_INTERVAL, this.AUTO_REFRESH_INTERVAL)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          const currentAccountId = this.merchantStore.accountId();
          if (!currentAccountId) {
            console.error('[MerchantService] AccountId lost, stopping auto-refresh');
            this.stopAutoRefresh();
            return of(null);
          }
          return this.loadMerchantDetails(currentAccountId, true);
        }),
      )
      .subscribe({
        error: (error) => {
          console.error('❌ [MerchantService] Auto-refresh failed:', error);
          // Continue auto-refresh even if one attempt fails
        },
      });
  }

  /**
   * Stop automatic refresh
   *
   * Called when:
   * - User logs out
   * - Service is destroyed
   */
  stopAutoRefresh(): void {
    this.isAutoRefreshActive = false;
  }

  /**
   * Manually refresh merchant data with rate limiting
   *
   * Implements throttling to prevent API abuse:
   * - If called within 2 minutes of last refresh, returns cached data
   * - Otherwise makes fresh API call
   * - Can be forced to bypass rate limiting (use sparingly)
   *
   * Use this when you need to update merchant data immediately
   * (e.g., after a transaction, after updating profile)
   *
   * @param accountId - Optional account ID. If not provided, uses stored accountId
   * @param force - Skip rate limit check and force API call (use carefully)
   * @returns Observable of merchant details response
   */
  refresh(accountId?: string, force = false): Observable<MerchantDetailsResponse> {
    const now = Date.now();
    const idToUse = accountId || this.merchantStore.accountId();

    if (!idToUse) {
      console.error('[MerchantService] No accountId provided or stored');
      throw new Error('AccountId is required for refresh');
    }

    // Rate limiting: Prevent too-frequent manual refreshes (unless forced)
    if (!force && now - this.lastRefreshTime < this.MIN_REFRESH_INTERVAL) {
      // Return cached data if available
      if (this.merchantStore.hasMerchant()) {
        return of({
          success: true,
          merchant: this.merchantStore.merchant()!,
          is_out_delivery_range: false,
          migrate_to_mobile_money: false,
        });
      }
    }

    // Update last refresh timestamp
    this.lastRefreshTime = now;

    return this.loadMerchantDetails(idToUse, true);
  }

  /**
   * Event-triggered refresh (respects rate limiting)
   *
   * Use this method for event-driven updates after user actions:
   * - After transaction completed
   * - After payment received
   * - After withdrawal made
   * - After profile updated
   * - When user navigates to dashboard
   *
   * Automatically respects rate limiting (2 minutes minimum between calls)
   * Returns cached data if called too frequently
   *
   * @param accountId - Optional account ID. If not provided, uses stored accountId
   * @returns Observable of merchant details response
   *
   * @example
   * ```typescript
   * // In your component after a transaction
   * onTransactionComplete(): void {
   *   this.merchantService.refreshOnEvent().subscribe();
   * }
   * ```
   */
  refreshOnEvent(accountId?: string): Observable<MerchantDetailsResponse> {
    return this.refresh(accountId, false); // Respects rate limiting
  }

  /**
   * Refresh only balance (lightweight update)
   *
   * Same as full refresh since the endpoint returns all data,
   * but semantically indicates balance-specific update
   *
   * @param accountId - Optional account ID. If not provided, uses stored accountId
   */
  refreshBalance(accountId?: string): Observable<MerchantDetailsResponse> {
    return this.refresh(accountId);
  }

  /**
   * Clear merchant data (called on logout)
   *
   * Clears:
   * - Merchant data from store
   * - Stops auto-refresh
   * - Resets error state
   */
  clearMerchantData(): void {
    this.merchantStore.clearMerchant();
    this.stopAutoRefresh();
  }

  /**
   * Check if merchant data is loaded
   */
  hasMerchantData(): boolean {
    return this.merchantStore.hasMerchant();
  }

  /**
   * Get current merchant ID
   */
  getMerchantId(): string {
    return this.merchantStore.merchantId();
  }
}
